import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Flashbar, FormField, Modal, Pagination, SpaceBetween, Spinner, Table, Textarea, TextFilter } from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Header from '@amzn/awsui-components-react/polaris/header';
import { ColumnDefinitions, PaginationLabels, TableEmptyState, TableNoMatchState } from './table-config';
import * as APIt from '../../../API';
import { useBundle } from '@amzn/react-arb-tools';
import PendingAccessRequestDetailsTablePanel from './DetailsTablePanel';
import { useQuery } from '@tanstack/react-query';
import { cancelAccessLevelRequest, queryVisitorAccessLevelApprovals } from './utils';
import { debug } from 'src/utils/commonUtils';

export interface RefreshCallBackInterface {
  (): Promise<void>;
}

export interface PendingAccessRequestsTablePanelPropsInterface {
  isTableLoading: boolean;
  refreshCallback: RefreshCallBackInterface;
  username: string;
  pendingRequestsAccessLevels: APIt.VisitorAccessLevel[];
}

export default function PendingAccessRequestsTablePanel(props: PendingAccessRequestsTablePanelPropsInterface ) {
  debug(`PendingAccessRequestsTablePanel() props is ${JSON.stringify(props)}`);

  const [allItems, setAllItems] = useState<APIt.VisitorAccessLevel[]>(props.pendingRequestsAccessLevels);
  const [cancelAccessRequestsVisible, setCancelAccessRequestsVisible] = useState<boolean>(false);
  const [cancellingAccessRequests, setCancellingAccessRequests] = useState<boolean>(false);
  const [hideTable, setHideTable] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<APIt.VisitorAccessLevel[]>([]);
  const [showCancelledAccessRequestsFlashbar, setShowCancelledAccessRequestsFlashbar] = useState<boolean>(false);

  const visitorAccessLevelApprovalsQuery = useQuery({
    queryKey: ['pendingAccessLevelApprovals'],
    queryFn: () => queryVisitorAccessLevelApprovals(selectedItems[0]?.id),
    retry: 3
  });

  const refresh = async () => {
    await visitorAccessLevelApprovalsQuery.refetch();
  };

  useEffect(() => {
    visitorAccessLevelApprovalsQuery.refetch();
  }, [selectedItems])
  
  useEffect(() => {
    setAllItems(props.pendingRequestsAccessLevels);
  }, [props.pendingRequestsAccessLevels]);

  const [bundle, isBundleLoading] = useBundle('components.SelfService.PendingAccessRequests.TablePanel');

  const pageSize = 10;

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    allItems,
    {
      filtering: {
        empty: <TableEmptyState title={!isBundleLoading ? bundle.getMessage('no-pending-access-requests-found') : 'No Pending Access Requests Found'} />,
        noMatch: <TableNoMatchState onClearFilter={() => actions.setFiltering('')} />
      },
      pagination: { pageSize: pageSize },
      sorting: {},
      selection: { trackBy: 'id' }
    }
  );

  const getFilterCounterText = (count: number) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  const FlashMessageAccessRequestsCancelled = () => (
    <Flashbar
      items = {
        [
          {
            type: 'success',
            dismissible: true,
            content: bundle.getMessage('access-request-cancelled'),
            onDismiss: () => setShowCancelledAccessRequestsFlashbar(false)
          },
        ]
      }
    />
  );

  const cancelAccessRequestsBtnClickHandler = () => {
    if (selectedItems.length > 0) setCancelAccessRequestsVisible(true);
  };

  const refreshBtnClickHandler = async () => {
    setRefreshing(true);
    await props.refreshCallback();
    setRefreshing(false);
  };

  const cancelAccessRequestsConfirmedHandler = async () => {
    setCancellingAccessRequests(true);
    let filteredSelectedItems: APIt.VisitorAccessLevel[] = selectedItems;
    try {
      for (let al of selectedItems) {
        await cancelAccessLevelRequest(al.id, props.username);
        filteredSelectedItems = filteredSelectedItems.filter((si) => si.id !== al.id);
      };
    } catch(error) {
      console.error(error);
    }
    setSelectedItems(filteredSelectedItems);
    setCancelAccessRequestsVisible(false);
    setCancellingAccessRequests(false);
    props.refreshCallback();
  };

  const itemsCount = (): number => {
    if (allItems) return allItems.length;
    return 0;
  };

  const ReasonField = () => {
    return(
      <>
      <FormField label={bundle.getMessage('reason-for-access-request')} >
        <Textarea value={selectedItems[0].reason} disabled/>
      </FormField>
      <br/>
      </>
    );
  };

  if (isBundleLoading) return <Spinner/>;

  return(
    <>
    {showCancelledAccessRequestsFlashbar && <FlashMessageAccessRequestsCancelled/>}
    <div id='tableDiv' hidden={hideTable}>
      <Table
        {...collectionProps}
        columnDefinitions={ColumnDefinitions}
        data-testid='PendingAccessRequestsTable'
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel={bundle.getMessage('filter-pending-access-requests')}
            filteringPlaceholder={bundle.getMessage('find-pending-access-requests')}
            countText={getFilterCounterText(filteredItemsCount === undefined ? 0: filteredItemsCount)}
          />
        }
        header={
          <Header
            counter={`(${itemsCount().toString()})`}
            actions={
              <>
              <SpaceBetween size='xs' direction='horizontal'>
                <Button loading={refreshing} key="refreshBtn" onClick={refreshBtnClickHandler} iconName='refresh'>{bundle.getMessage('refresh')}</Button>
              </SpaceBetween>
              </>
            }
          >
            {bundle.getMessage('pending-access-requests')}
          </Header>
        }
        footer={
          <Header
            actions={
              <>
              <SpaceBetween size='xs' direction='horizontal'>
                <Button
                  data-testid='PendingAccessRequestsCancelAccessRequestButton'
                  disabled={selectedItems.length == 0}
                  onClick={cancelAccessRequestsBtnClickHandler}
                  variant='primary'
                >
                  {bundle.getMessage('cancel-access-request')}
                </Button>
              </SpaceBetween>
              </>
            }
          >
          </Header>
        }
        items={items}
        loading={props.isTableLoading}
        loadingText={bundle.getMessage('loading-pending-access-requests')}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems) }
        pagination={
          allItems.length > pageSize
          &&
          <Pagination
            {...paginationProps}
            ariaLabels={PaginationLabels}
          />
        }
        resizableColumns={true}
        selectedItems={selectedItems}
        selectionType='multi'
        stickyHeader={true}
        trackBy='id'
      />
    </div>
    {
      selectedItems.length == 1
      &&
      <Container
        key={'PendingAccessRequestDetails'}
        header={<Header>{`${bundle.getMessage('access-request-details')} for ${selectedItems[0].person_id} ${selectedItems[0].access_level_name}`}</Header>}
        children={
          <>
          <ReasonField/>
          {selectedItems.length == 1
          && visitorAccessLevelApprovalsQuery.data
          &&
            <PendingAccessRequestDetailsTablePanel
              isLoading={visitorAccessLevelApprovalsQuery.isLoading || visitorAccessLevelApprovalsQuery.isFetching}
              refreshCallback={refresh}
              visitorAccessLevelApprovals={visitorAccessLevelApprovalsQuery.data}
            />}
          </>
        }
      />
    }
    {cancelAccessRequestsVisible
    &&
    <Modal
      onDismiss={() => setCancelAccessRequestsVisible(false)}
      visible={cancelAccessRequestsVisible}
      closeAriaLabel='Close'
      size='medium'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={() => setCancelAccessRequestsVisible(false)}>{bundle.getMessage('no')}</Button>
            <Button
              disabled={cancellingAccessRequests}
              loading={cancellingAccessRequests}
              onClick={() => cancelAccessRequestsConfirmedHandler() }
              variant='primary'
            >
              {bundle.getMessage('yes')}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={bundle.getMessage('confirm-access-request-cancel')}
    >
      {bundle.getMessage('please-confirm-access-request-cancel')}
    </Modal>
    }
    </>
  );
}
